import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {useGetChecklistDefinitionQuery, useUpdateChecklistDefinitionMutation} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {ChecklistForm} from '../../components/ChecklistForm';
import {toUpdateChecklistDefinitionBody} from '../../utils/toChecklistDefinitionRequestBody';
import {toChecklistFormValue} from '../../utils/toChecklistFormValue';

export function Detail() {
  const {id: checklistId} = useParams<{id: string}>();
  const navigate = useNavigate();

  const checklistDetailQuery = useGetChecklistDefinitionQuery(
    isNil(checklistId) ? skipToken : {definitionId: checklistId}
  );

  const [updateChecklistDefinition, updateChecklistDefinitionQuery] =
    useUpdateChecklistDefinitionMutation();

  if (isNil(checklistId)) {
    return null;
  }

  return (
    <DataStatus isLoading={checklistDetailQuery.isLoading} isError={checklistDetailQuery.isError}>
      <SettingsTemplate
        header={{
          title: checklistDetailQuery.data?.name || '',
          breadcrumbs: [
            {
              label: i18n.t('page.generalSettings.labels.title'),
              isCurrentPage: true,
            },
            {
              label: i18n.t('page.settings.generalChecklist.title'),
              href: settingsRoutes.generalChecklist,
            },
          ],
        }}
        data-testid={testIds.settings.generalChecklistDetail()}
      >
        <Show when={checklistDetailQuery.data}>
          {(data) => (
            <ChecklistForm
              key={data.id}
              defaultValues={toChecklistFormValue(data)}
              onSubmit={async (formData, _setErrors, _reset, blockerContext) => {
                await updateChecklistDefinition({
                  definitionId: checklistId,
                  body: toUpdateChecklistDefinitionBody(formData),
                })
                  .unwrap()
                  .then(() => {
                    if (blockerContext?.wasSubmittedFromBlocker) {
                      return;
                    }
                    navigate(settingsRoutes.generalChecklist);
                  })
                  .catch((e: ApiError) =>
                    handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
                  );
              }}
              isLoading={updateChecklistDefinitionQuery.isLoading}
              isEditing
            />
          )}
        </Show>
      </SettingsTemplate>
    </DataStatus>
  );
}
